// Memoized theme styles hook to reduce Emotion CSS overhead
import { useMemo } from 'react';
import { useColorMode } from '@chakra-ui/react';

// Pre-computed style constants
const STYLE_CONSTANTS = {
  dark: {
    bg: {
      primary: 'primary.300',
      secondary: 'gray.800',
      surface: 'gray.700'
    },
    text: {
      primary: 'white',
      secondary: 'gray.300',
      label: 'orange.700',
      labelHover: 'orange.500'
    },
    shadow: {
      default: '0 1px 3px rgba(0, 0, 0, 0.12)',
      elevated: '0 4px 6px rgba(0, 0, 0, 0.1)'
    }
  },
  light: {
    bg: {
      primary: 'white.500',
      secondary: 'gray.50',
      surface: 'white'
    },
    text: {
      primary: 'black',
      secondary: 'gray.700',
      label: 'blue.500',
      labelHover: 'blue.400'
    },
    shadow: {
      default: '0 1px 2px rgba(0, 0, 0, 0.24)',
      elevated: '0 4px 6px rgba(0, 0, 0, 0.15)'
    }
  }
};

// Cache for computed styles
const styleCache = new Map();

export const useThemedStyles = () => {
  const { colorMode } = useColorMode();
  
  return useMemo(() => {
    const cacheKey = `theme-${colorMode}`;
    
    if (styleCache.has(cacheKey)) {
      return styleCache.get(cacheKey);
    }
    
    const theme = STYLE_CONSTANTS[colorMode];
    
    const styles = {
      // Flex container styles
      flexContainer: {
        bgColor: theme.bg.primary,
        boxShadow: theme.shadow.default,
        width: '100%'
      },
      
      // Text styles
      label: {
        color: theme.text.label,
        _hover: { color: theme.text.labelHover }
      },
      
      value: {
        color: theme.text.primary
      },
      
      // Surface styles
      surface: {
        bg: theme.bg.surface,
        shadow: theme.shadow.default
      },
      
      // Get raw theme values
      colors: theme,
      
      // Helper functions
      getColor: (type, variant = 'primary') => theme[type]?.[variant] || theme.text.primary,
      getShadow: (variant = 'default') => theme.shadow[variant]
    };
    
    // Cache the computed styles
    if (styleCache.size > 10) {
      // Clear old entries to prevent memory bloat
      const firstKey = styleCache.keys().next().value;
      styleCache.delete(firstKey);
    }
    styleCache.set(cacheKey, styles);
    
    return styles;
  }, [colorMode]);
};

// Specialized hook for data tiles
export const useDataTileStyles = () => {
  const { colorMode } = useColorMode();
  
  return useMemo(() => {
    const isDark = colorMode === 'dark';
    
    return {
      container: {
        direction: 'column',
        mb: '12px',
        py: '18px',
        px: '20px',
        flex: 1,
        borderRadius: 3,
        bgColor: isDark ? 'primary.300' : 'white.500',
        boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.12)' : '0 1px 2px rgba(0, 0, 0, 0.24)'
      },
      label: {
        fontSize: { base: '11px', md: '12px' },
        color: isDark ? 'orange.700' : 'blue.500',
        fontWeight: '700',
        lineHeight: 'none',
        textTransform: 'uppercase'
      },
      value: {
        fontSize: { base: '18px', md: '20px' },
        fontWeight: '700',
        lineHeight: 'none',
        color: isDark ? 'white' : 'black'
      }
    };
  }, [colorMode]);
};

// Hook for chart styles with cleanup awareness
export const useChartStyles = () => {
  const { colorMode } = useColorMode();
  
  return useMemo(() => {
    const isDark = colorMode === 'dark';
    
    return {
      container: {
        bg: isDark ? 'gray.800' : 'white',
        borderRadius: 'md',
        p: 4
      },
      axis: {
        stroke: isDark ? '#718096' : '#A0AEC0',
        fontSize: 12
      },
      grid: {
        stroke: isDark ? '#2D3748' : '#E2E8F0'
      },
      tooltip: {
        bg: isDark ? 'gray.700' : 'white',
        border: `1px solid ${isDark ? '#4A5568' : '#E2E8F0'}`,
        borderRadius: 'md',
        boxShadow: 'lg'
      }
    };
  }, [colorMode]);
};